// wsVersion1EdgedocsWsClient.ts
// Clean Socket.IO client for EdgeDocs WS v1 protocol with proper event handling

import { io, Socket } from 'socket.io-client';

type UpdateHandler = (payload: any) => void;
type ConnectionHandler = () => void;
type ErrorHandler = (error: any) => void;

interface ClientOptions {
  token?: string;
  realmId?: string;
}

export class WsVersion1EdgedocsWsClient {
  private socket: Socket | null = null;
  private updateHandler: UpdateHandler | null = null;
  private initialHandler: UpdateHandler | null = null;
  private openHandler: ConnectionHandler | null = null;
  private closeHandler: ConnectionHandler | null = null;
  private errorHandler: ErrorHandler | null = null;
  private opAckHandler: UpdateHandler | null = null;
  private opRejectHandler: UpdateHandler | null = null;
  private isConnected: boolean = false;
  private currentVersionId: string | null = null; // Track version ID for delta operations

  constructor(private baseUrl: string, private auth: ClientOptions) {}

  connect(): this {
    if (this.socket) {
      this.socket.disconnect();
    }

    // Ensure we connect to the collaboration namespace
    const namespaceUrl = this.baseUrl.includes('/collaboration') 
      ? this.baseUrl 
      : `${this.baseUrl.replace(/\/$/, '')}/collaboration`;

    this.socket = io(namespaceUrl, {
      path: '/socket.io',
      withCredentials: true,
      auth: { 
        token: this.auth.token, 
        realmId: this.auth.realmId 
      },
      forceNew: true,
      timeout: 10000,
    });

    // Connection state management
    this.socket.on('connect', () => {
      console.log('[EdgeDocs] WebSocket connected:', this.socket?.id);
      this.isConnected = true;
      if (this.openHandler) this.openHandler();
    });

    this.socket.on('connect_error', (error) => {
      console.error('[EdgeDocs] Connection error:', error.message);
      this.isConnected = false;
      if (this.errorHandler) this.errorHandler(error);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[EdgeDocs] Disconnected:', reason);
      this.isConnected = false;
      if (this.closeHandler) this.closeHandler();
    });

    this.socket.on('error', (error) => {
      console.error('[EdgeDocs] Socket error:', error);
      if (this.errorHandler) this.errorHandler(error);
    });

    // EdgeDocs protocol events
    this.socket.on('edgedocs:initial', (msg: any) => {
      console.log('[EdgeDocs] Received initial state');
      if (this.initialHandler) this.initialHandler(msg);
      // Also forward to generic update handler with an event marker for consumers that rely on a single stream
      if (this.updateHandler) this.updateHandler({ __eventType: 'initial', ...msg });
    });

    this.socket.on('edgedocs:update', (msg: any) => {
      console.log('[EdgeDocs] Received update');
      if (this.updateHandler) this.updateHandler({ __eventType: 'update', ...msg });
    });

    this.socket.on('edgedocs:op-ack', (msg: any) => {
      console.log('[EdgeDocs] Operation acknowledged:', msg?.opId);
      if (this.opAckHandler) this.opAckHandler(msg);
    });

    this.socket.on('edgedocs:op-reject', (msg: any) => {
      console.warn('[EdgeDocs] Operation rejected:', msg?.reason);
      if (this.opRejectHandler) this.opRejectHandler(msg);
    });

    return this;
  }

  // Event handler registration
  onUpdate(handler: UpdateHandler): this {
    this.updateHandler = handler;
    return this;
  }

  onInitial(handler: UpdateHandler): this {
    this.initialHandler = handler;
    return this;
  }

  onOpen(handler: ConnectionHandler): this {
    this.openHandler = handler;
    return this;
  }

  onClose(handler: ConnectionHandler): this {
    this.closeHandler = handler;
    return this;
  }

  onError(handler: ErrorHandler): this {
    this.errorHandler = handler;
    return this;
  }

  onOpAck(handler: UpdateHandler): this {
    this.opAckHandler = handler;
    return this;
  }

  onOpReject(handler: UpdateHandler): this {
    this.opRejectHandler = handler;
    return this;
  }

  // Document operations
  join(documentId: string): void {
    if (!this.socket || !this.isConnected) {
      console.warn('[EdgeDocs] Cannot join - not connected');
      return;
    }
    console.log('[EdgeDocs] Joining document:', documentId);
    this.currentVersionId = null; // Clear version ID for document-level operations
    this.socket.emit('edgedocs:join', { documentId });
  }

  joinVersion(documentId: string, versionId: string): void {
    if (!this.socket || !this.isConnected) {
      console.warn('[EdgeDocs] Cannot join version - not connected');
      return;
    }
    console.log(`[EdgeDocs] Joining version-specific room: ${documentId}:${versionId}`);
    this.currentVersionId = versionId; // Store version ID for delta operations
    this.socket.emit('edgedocs:join', { documentId, versionId });
  }

  getInitial(documentId: string): void {
    if (!this.socket || !this.isConnected) {
      console.warn('[EdgeDocs] Cannot get initial - not connected');
      return;
    }
    console.log('[EdgeDocs] Requesting initial state for:', documentId);
    this.socket.emit('edgedocs:get-initial', { documentId });
  }

  getInitialVersion(documentId: string, versionId: string): void {
    if (!this.socket || !this.isConnected) {
      console.warn('[EdgeDocs] Cannot get initial version - not connected');
      return;
    }
    console.log(`[EdgeDocs] Requesting initial state for version: ${documentId}:${versionId}`);
    this.socket.emit('edgedocs:get-initial', { documentId, versionId });
  }

  sendDelta(documentId: string, delta: any, opId?: string, clientVersion?: number): void {
    if (!this.socket || !this.isConnected) {
      console.warn('[EdgeDocs] Cannot send delta - not connected');
      return;
    }
    const payload = { 
      documentId, 
      delta, 
      opId: opId || `op-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      clientVersion,
      ...(this.currentVersionId && { versionId: this.currentVersionId }) // Include versionId if this is a version-specific client
    };
    
    // 🔍 ENHANCED LOGGING: Capture WebSocket-level delta transmission
    console.log('🌐 [WsVersion1EdgedocsWsClient] SENDING DELTA TO SERVER:', {
      socketId: this.socket.id,
      documentId,
      versionId: this.currentVersionId,
      opId: payload.opId,
      clientVersion,
      deltaOperation: delta.operation,
      deltaContent: delta.content,
      deltaPosition: delta.position,
      isConnected: this.isConnected,
      socketConnected: this.socket.connected,
      timestamp: new Date().toISOString()
    });
    
    this.socket.emit('edgedocs:delta', payload);
    
    console.log('📡 [WsVersion1EdgedocsWsClient] Delta emitted to server via edgedocs:delta event');
  }

  sendCursor(documentId: string, cursor_position?: any, selection_data?: any): void {
    if (!this.socket || !this.isConnected) return;
    this.socket.emit('edgedocs:cursor', { documentId, cursor_position, selection_data });
  }

  disconnect(): void {
    if (this.socket) {
      console.log('[EdgeDocs] Disconnecting...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  // Connection state
  get connected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }
}